import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const TICKET_TOKEN_KEY = 'wuw_ticket_token';

/**
 * True when festival/bus ticketing should be shown: either the edition has no
 * access token (public) or the visitor's stored token matches it.
 */
export const isTicketingUnlocked = (accessToken: string | undefined, ticketToken: string | null) =>
  !accessToken || ticketToken === accessToken;

/**
 * Captures a festival access token from the `?c=<token>` query param, persists
 * it to localStorage, and returns the stored token. Pages compare it against
 * the active edition's `accessToken` (via `isTicketingUnlocked`) to decide
 * whether to show ticket UI.
 */
export const useTicketing = () => {
  const [params, setParams] = useSearchParams();
  const capturedToken = params.get('c');
  const [ticketToken, setTicketToken] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : window.localStorage.getItem(TICKET_TOKEN_KEY),
  );

  useEffect(() => {
    if (!capturedToken) return;
    window.localStorage.setItem(TICKET_TOKEN_KEY, capturedToken);
    setTicketToken(capturedToken);
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('c');
        return next;
      },
      { replace: true },
    );
  }, [capturedToken, setParams]);

  return { ticketToken };
};
